import React, { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { api, thumbUrl, posterUrl, displayTitle } from "@/lib/api";
import { ownerRowState, ownerLocationLabel, copyText, xPostProblems } from "@/lib/owner";
import { toast } from "sonner";
import { Check, Copy, Loader2, MapPin, Pencil, ChevronRight, RefreshCw, Video } from "lucide-react";
import { cn } from "@/lib/utils";

const STATE_CLASS = { ready: "text-emerald-300", failed: "text-red-300", processing: "text-amber-200", captioning: "text-amber-200" };
const LOC_CLASS = { exact: "text-foreground", general: "text-foreground", verify: "text-amber-200", pending: "text-amber-200", none: "text-muted-foreground" };

export const OwnerUploadRow = ({ m, focused, onPick, onEdit, onNext, onChanged }) => {
  const st = ownerRowState(m);
  const loc = ownerLocationLabel(m);
  const [copied, setCopied] = useState(false);
  const [retrying, setRetrying] = useState(false);
  useEffect(() => { if (!copied) return; const t = setTimeout(() => setCopied(false), 2500); return () => clearTimeout(t); }, [copied]);

  const copyForX = async () => {
    const text = (m.x_post || "").trim();   // STORED x_post only — never regenerated on tap
    if (xPostProblems(text).length) { toast.error("X text not ready — use Retry caption"); return; }
    if (await copyText(text)) { setCopied(true); toast.success("Copied for X"); }
    else toast.error("Could not copy — long-press the text to copy manually");
  };

  // Retry = X_POST ONLY: the proposal-only /ai/caption (writes nothing) supplies a fresh x_post and ONLY that field is saved.
  // /ai/recaption is deliberately NOT used here — it can fill a blank title/description, which this button must never do.
  const retryCaption = async () => {
    setRetrying(true);
    try {
      const p = await api.post("/ai/caption", { media_id: m.id, mode: "rewrite" }, { timeout: 120000 });
      const x = (p.data?.x_post || "").trim();
      const bad = xPostProblems(x);
      if (bad.length) throw new Error(x ? `X text rejected (${bad.join(", ")})` : "no X text produced");
      await api.patch(`/media/${m.id}`, { x_post: x });
      toast.success("X text ready");
      onChanged?.();
    } catch (e) {
      toast.error(`Retry failed: ${e?.response?.data?.detail?.message || e?.response?.data?.detail || e.message}`);
    } finally { setRetrying(false); }
  };

  const title = displayTitle(m.title) || m.title || "Untitled";
  const img = m.media_type === "video" ? posterUrl(m, 200) : thumbUrl(m.file_ref, 200);
  return (
    <div id={`owner-row-${m.id}`} data-testid={`owner-row-${m.id}`}
      className={cn("rounded-xl border bg-card/70 p-3 flex gap-3 transition-colors", focused ? "border-primary/70 shadow-md shadow-primary/10" : "border-border")}>
      <div className="w-16 h-16 rounded-lg overflow-hidden bg-black shrink-0 relative cursor-pointer" onClick={onPick} data-testid={`owner-pick-${m.id}`} role="button">
        {img ? <img src={img} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-muted-foreground"><Video className="w-5 h-5" /></div>}
        {m.media_type === "video" && <span className="absolute bottom-1 left-1 text-[9px] uppercase font-bold px-1 rounded bg-black/70">video</span>}
      </div>
      <div className="min-w-0 flex-1 space-y-1.5">
        <div className="text-sm font-medium truncate cursor-pointer" data-testid={`owner-title-${m.id}`} onClick={onPick}>{title}</div>
        {m.reprocess?.status === "blocked" && <div className="text-[11px] text-sky-300" data-testid={`owner-job-${m.id}`} data-state="blocked">Reprocess blocked · media storage unavailable</div>}
        {m.reprocess?.status === "failed" && <div className="text-[11px] text-red-300 truncate" data-testid={`owner-job-${m.id}`} data-state="failed">Reprocess failed · {m.reprocess.error}</div>}
        {m.reprocess?.status === "processing" && <div className="text-[11px] text-amber-200" data-testid={`owner-job-${m.id}`} data-state="processing">Reprocessing…</div>}
        <div className={cn("text-xs flex items-center gap-1.5", STATE_CLASS[st.key])} data-testid={`owner-status-${m.id}`} data-state={st.key}>
          {(st.key === "processing" || st.key === "captioning") && <Loader2 className="w-3 h-3 animate-spin" />}
          {st.key === "ready" && <Check className="w-3 h-3" />}
          {st.label}
        </div>
        <div className={cn("text-[11px] flex items-start gap-1", LOC_CLASS[loc.key])} data-testid={`owner-location-${m.id}`} data-loc={loc.key}>
          <MapPin className="w-3 h-3 mt-0.5 shrink-0" /><span className="truncate">{loc.label}{loc.key === "exact" ? " · Exact location" : loc.key === "general" ? " · General location" : loc.key === "verify" ? " · Location needs verification" : ""}</span>
        </div>
        <div className="flex items-center gap-2 pt-1">
          {st.key === "ready" ? (
            <Button type="button" data-testid={`owner-copy-x-${m.id}`} onClick={copyForX}
              className={cn("flex-1 h-11 rounded-full font-semibold", copied && "bg-emerald-600 hover:bg-emerald-600 text-white")}>
              {copied ? <><Check className="w-4 h-4 mr-1.5" /> Copied for X</> : <><Copy className="w-4 h-4 mr-1.5" /> Copy for X</>}
            </Button>
          ) : st.key === "failed" ? (
            <Button type="button" variant="secondary" data-testid={`owner-retry-caption-${m.id}`} onClick={retryCaption} disabled={retrying} className="flex-1 h-11 rounded-full font-semibold">
              {retrying ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Generating…</> : <><RefreshCw className="w-4 h-4 mr-1.5" /> Retry caption</>}
            </Button>
          ) : (
            <Button type="button" disabled data-testid={`owner-copy-x-${m.id}`} className="flex-1 h-11 rounded-full font-semibold opacity-60">
              <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> {st.label}
            </Button>
          )}
          <button type="button" data-testid={`owner-edit-${m.id}`} onClick={onEdit} aria-label="Edit"
            className="h-11 w-11 rounded-full border border-border bg-secondary/40 flex items-center justify-center text-muted-foreground hover:text-foreground"><Pencil className="w-4 h-4" /></button>
          <button type="button" data-testid={`owner-next-${m.id}`} onClick={onNext} aria-label="Next"
            className="h-11 w-11 rounded-full border border-border bg-secondary/40 flex items-center justify-center text-muted-foreground hover:text-foreground"><ChevronRight className="w-5 h-5" /></button>
        </div>
      </div>
    </div>
  );
};
